import React, { useEffect, useState } from 'react';
import { useHilFormRenderer } from '@bizfirst/hil-ui';
import type { HilChatMessageRendererProps } from '../settings/HilChatWindowSettings';

interface FetchedSchemaState {
  loading: boolean;
  schema: unknown | null;
  error: string | null;
}

/**
 * Branch 2 of the fallback chain: no app-registered HilFormRendererProvider content,
 * but settings.api.formFetchURL is supplied. Fetches the raw schema and renders it as
 * an editable JSON blob — the same tier hil-ui's FormStepRenderer falls back to when
 * no renderer is registered, just backed by a real fetch instead of a static placeholder.
 * A full widget-based renderer is out of scope here; that is exactly what an app-registered
 * HilFormRendererProvider content component (branch 1) is for.
 */
function FetchedFormFallback({ message, settings, disabled, onFormValuesChange }: HilChatMessageRendererProps): React.JSX.Element {
  const [state, setState] = useState<FetchedSchemaState>({ loading: true, schema: null, error: null });
  const [formData, setFormData] = useState<Record<string, unknown>>(message.prePopulate ?? {});

  useEffect(() => {
    let cancelled = false;
    const url = settings.api?.formFetchURL;
    if (!url || message.formID === null) {
      setState({ loading: false, schema: null, error: null });
      return;
    }

    setState({ loading: true, schema: null, error: null });
    const token = settings.api?.authTokenProvider?.() ?? null;

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ formID: message.formID }),
    })
      .then(resp => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return resp.json();
      })
      .then(json => {
        if (!cancelled) setState({ loading: false, schema: json ?? null, error: null });
      })
      .catch((e: Error) => {
        if (!cancelled) setState({ loading: false, schema: null, error: e.message });
      });

    return () => {
      cancelled = true;
    };
  }, [settings.api, message.formID]);

  const handleChange = (value: string) => {
    try {
      const parsed = JSON.parse(value);
      setFormData(parsed);
      onFormValuesChange?.(parsed);
    } catch {
      // ignore invalid JSON while typing
    }
  };

  if (state.loading) return <div className="hil-info-block">Loading form…</div>;
  if (state.error) return <div className="hil-info-block hil-chat-bubble__status--failed">{state.error}</div>;

  return (
    <div className="hil-info-block">
      <label className="hil-label" htmlFor={`hil-chat-form-${message.id}`}>Form Data</label>
      <textarea
        id={`hil-chat-form-${message.id}`}
        className="hil-textarea"
        style={{ minHeight: 120 }}
        defaultValue={JSON.stringify(formData, null, 2)}
        onChange={e => handleChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

export function HilChatFormMessage(props: HilChatMessageRendererProps): React.JSX.Element {
  const { message, settings, disabled, onFormValuesChange } = props;
  const formRenderer = useHilFormRenderer();

  if (message.formID && formRenderer) {
    return (
      <>
        {formRenderer({
          formID: message.formID,
          prePopulate: message.prePopulate,
          onValuesChange: values => onFormValuesChange?.(values),
          disabled,
        })}
      </>
    );
  }

  if (message.formID && settings.api?.formFetchURL) {
    return <FetchedFormFallback {...props} />;
  }

  return (
    <div className="hil-info-block">
      <p>Atlas Form #{message.formID} — register a HilFormRendererProvider content component or settings.api.formFetchURL.</p>
    </div>
  );
}
